/** A GDAL failure as gdal3.js reports it. */
interface GdalError {
  no?: number;
  message?: string;
}

const isGdalError = (value: unknown): value is GdalError => typeof value === "object" && value !== null && ("message" in value || "no" in value);

const describe = (reason: unknown): string => {
  if (Array.isArray(reason)) return reason.map(describe).filter(Boolean).join("; ");

  if (reason instanceof Error) return reason.message;

  if (isGdalError(reason)) {
    const code = typeof reason.no === "number" && reason.no > 0 ? ` (CPLE ${reason.no})` : "";
    return `${reason.message ?? ""}${code}`;
  }

  return typeof reason === "string" ? reason : "";
};

/**
 * A readable description of a gdal3.js failure.
 *
 * Rejections arrive as a `{ no, message }` object, or an array of them when a file
 * fails to open. Neither is an Error, so they otherwise stringify to "[object Object]".
 */
export const describeGdalFailure = (reason: unknown): string => describe(reason) || "no detail reported";

/** Thrown when the GDAL runtime could not be downloaded or started. */
export class GdalLoadError extends Error {
  constructor(public readonly reason: unknown) {
    super(`GDAL runtime failed to load: ${describeGdalFailure(reason)}`);
    this.name = "GdalLoadError";
  }
}
